// ============================================================================
// RADAR DASHBOARD PAGE — Redesigned with BRIO Design System
// ============================================================================
// Operational command center for the practice. Scans the app for overdue
// actions, silent failures, operational drift, and financial risks.
//
// Uses BRIO components: BrioFrame, BrioAnalyticsSurface, BrioListItem (alert),
// BrioBadge, BrioProgress, BrioPageHeader, BrioListWrapper, BrioModal
//
// Features: multiselect, bulk actions, dismiss, time-bucketed domain grouping
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  AlertTriangle, Clock, Eye, EyeOff,
  ChevronDown, ChevronRight, Shield, TrendingDown,
  Activity, Users, Briefcase, Building2, UserPlus, Database,
  X, Check, Trash2, RefreshCw, CheckSquare, Square, MinusSquare,
  Archive, FolderOpen, Mail, Loader, CheckCircle, FileText,
} from 'lucide-react'; // eslint-disable-line no-unused-vars
import {
  archiveMonthlyTaxInvoices,
  markSentToAccountant,
  buildAccountantEmailSummary,
  isSharePointConfigured,
} from '../../utils/invoiceStorageService';
import {
  BrioFrame,
  BrioAnalyticsSurface,
  BrioListItem,
  BrioListWrapper,
  BrioBadge,
  BrioProgress,
  BrioPageHeader,
  BrioModal,
  BrioModalBody,
} from '../../components/BrioComponents';
import { useRadar } from '../../hooks/useRadar';
import { useHFBTeamAssignments } from '../../hooks/useHFBData';
import { useDataIntegrityFindings } from '../../hooks/useDataIntegrityFindings';
import { usePressFolderAlerts } from '../../hooks/usePressFolderAlerts';
import { resolveTemplate } from '../../utils/emailTemplates';

function getCurrentISOWeekId() {
  const d = new Date();
  const dUTC = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = dUTC.getUTCDay() || 7;
  dUTC.setUTCDate(dUTC.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(dUTC.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil((((dUTC - yearStart) / 86400000) + 1) / 7);
  return dUTC.getUTCFullYear() + '-W' + String(weekNum).padStart(2, '0');
}

// ── Constants & Mappings ─────────────────────────────────────────────────────

const DOMAIN_LABELS = {
  finance: 'Finance',
  projects: 'Projects',
  bd: 'Business Development',
  hiring: 'Hiring',
  team: 'Team',
  system: 'System / Data',
};

const DOMAIN_ICONS = {
  finance: Building2,
  projects: Briefcase,
  bd: TrendingDown,
  hiring: UserPlus,
  team: Users,
  system: Database,
};

// Map severity to BrioBadge variants and BRIO CSS colors
const SEVERITY_MAP = {
  critical: { badge: 'danger',  color: 'var(--brio-red)',    tone: 'red',    label: 'Critical' },
  warning:  { badge: 'caution', color: 'var(--brio-gold)',   tone: 'gold',   label: 'Warning' },
  signal:   { badge: 'info',    color: 'var(--brio-teal)',   tone: 'green',  label: 'Signal' },
};

const STATUS_MAP = {
  open:         { badge: 'neutral',  label: 'Open' },
  acknowledged: { badge: 'info',     label: 'Acknowledged' },
  snoozed:      { badge: 'pending',  label: 'Snoozed' },
  resolved:     { badge: 'approved', label: 'Resolved' },
  dismissed:    { badge: 'neutral',  label: 'Dismissed' },
};

const DRIFT_STATUS = {
  green: { color: 'var(--brio-green)', label: 'Healthy' },
  amber: { color: 'var(--brio-gold)',  label: 'Watch' },
  red:   { color: 'var(--brio-red)',   label: 'Alert' },
};

const BUCKET_LABELS = { today: 'Today', this_week: 'This Week', upcoming: 'Upcoming' };

const font = "'Raleway', system-ui, sans-serif";

// Shared inline styles following BRIO conventions
const filterBtn = (active) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 10px', border: '1px solid var(--brio-border)',
  background: active ? 'var(--brio-gold-light)' : 'transparent',
  color: active ? 'var(--brio-gold)' : 'var(--brio-grey)',
  fontWeight: active ? 400 : 300, fontSize: 11, fontFamily: font,
  cursor: 'pointer', transition: 'all 0.15s',
});

const actionBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 8px', border: '1px solid var(--brio-border)',
  background: 'transparent', color: 'var(--brio-grey)',
  fontSize: 10, fontWeight: 300, fontFamily: font,
  cursor: 'pointer', transition: 'all 0.15s',
};

const actionBtnGold = {
  ...actionBtn, background: 'var(--brio-gold)', color: '#fff',
  border: '1px solid var(--brio-gold)',
};

// ── Score & Drift ────────────────────────────────────────────────────────────

function ScoreBlock({ score, label }) {
  // Map score to BrioProgress variant
  let variant = 'normal';
  if (score < 60) variant = 'danger';
  else if (score < 80) variant = 'warning';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 120 }}>
        <BrioProgress value={score} variant={variant} tall />
      </div>
      <div>
        <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--brio-black)', fontFamily: font }}>
          {score}
        </span>
        <span style={{ fontSize: 11, fontWeight: 300, color: 'var(--brio-grey)', marginLeft: 6, fontFamily: font }}>
          {label}
        </span>
      </div>
    </div>
  );
}

function DriftRow({ domainHealth }) {
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {Object.entries(domainHealth).map(([domain, status]) => {
        const cfg = DRIFT_STATUS[status] || DRIFT_STATUS.green;
        const Icon = DOMAIN_ICONS[domain] || Activity;
        return (
          <div key={domain} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            fontSize: 11, fontWeight: 300, color: cfg.color, fontFamily: font,
          }}>
            <Icon size={12} />
            <span>{DOMAIN_LABELS[domain]}</span>
          </div>
        );
      })}
    </div>
  );
}

function PrimaryRiskBlock({ item, fmtCurr }) {
  if (!item) return null;
  const sev = SEVERITY_MAP[item.severity] || SEVERITY_MAP.warning;

  return (
    <BrioListItem variant="alert" tone={sev.tone}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', padding: '4px 0' }}>
        <AlertTriangle size={15} style={{ color: sev.color, marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 500, color: sev.color, textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: font }}>
            Primary Risk
          </div>
          <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--brio-black)', fontFamily: font }}>{item.title}</div>
          <div style={{ fontSize: 11, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>{item.description}</div>
        </div>
        {item.financial_impact != null && fmtCurr && (
          <div style={{ fontSize: 12, fontWeight: 400, color: sev.color, whiteSpace: 'nowrap', fontFamily: font }}>
            {fmtCurr(item.financial_impact)}
          </div>
        )}
      </div>
    </BrioListItem>
  );
}

// ── Radar Item Row ───────────────────────────────────────────────────────────

function RadarItemRow({ item, fmtCurr, selected, hasSelection, onSelect, onAcknowledge, onSnooze, onResolve, onDismiss, onNavigate }) {
  const sev = SEVERITY_MAP[item.severity] || SEVERITY_MAP.warning;
  const isActioned = item.status !== 'open';

  return (
    <BrioListItem
      variant="alert"
      tone={sev.tone}
      hoverable
      style={{ opacity: isActioned ? 0.6 : 1 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '2px 0' }}>
        {/* Checkbox for multiselect — passes event for shift-click range selection */}
        <div
          style={{ flexShrink: 0, cursor: 'pointer', color: 'var(--brio-grey)', display: 'flex' }}
          onClick={(e) => { e.stopPropagation(); onSelect(item, e); }}
        >
          {selected
            ? <CheckSquare size={15} style={{ color: 'var(--brio-gold)' }} />
            : <Square size={15} />
          }
        </div>

        {/* Main content — clickable to navigate or open workflow */}
        <div
          style={{ flex: 1, minWidth: 0, cursor: (item.navigation_target || item.type === 'ca_monthly_reminder') ? 'pointer' : 'default' }}
          onClick={() => onNavigate && onNavigate(item)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--brio-black)', fontFamily: font }}>
              {item.title}
            </span>
            <BrioBadge variant={sev.badge}>{sev.label}</BrioBadge>
            {item.is_silent_failure && (
              <BrioBadge variant="caution">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <EyeOff size={9} /> Silent failure
                </span>
              </BrioBadge>
            )}
            {isActioned && <BrioBadge variant={STATUS_MAP[item.status]?.badge || 'neutral'}>{STATUS_MAP[item.status]?.label}</BrioBadge>}
          </div>
          <div style={{ fontSize: 11, fontWeight: 300, color: 'var(--brio-grey)', marginTop: 2, fontFamily: font }}>
            {item.description}
          </div>
        </div>

        {/* Financial impact */}
        {item.financial_impact != null && fmtCurr && (
          <div style={{ fontSize: 11, fontWeight: 400, color: sev.color, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: font }}>
            {fmtCurr(item.financial_impact)}
          </div>
        )}

        {/* Days overdue */}
        {item.days_overdue > 0 && (
          <div style={{ fontSize: 10, fontWeight: 300, color: sev.color, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: font }}>
            {item.days_overdue}d
          </div>
        )}

        {/* Inline quick actions — hidden when bulk selection is active */}
        {!hasSelection && (
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
            {item.status !== 'acknowledged' && (
              <button style={actionBtn} onClick={() => onAcknowledge(item)} title="Acknowledge">
                <Eye size={11} />
              </button>
            )}
            <button style={actionBtn} onClick={() => onSnooze(item)} title="Snooze 7 days">
              <Clock size={11} />
            </button>
            <button style={actionBtn} onClick={() => onResolve(item)} title="Resolve">
              <Check size={11} />
            </button>
            <button style={{ ...actionBtn, color: 'var(--brio-red)' }} onClick={() => onDismiss(item)} title="Dismiss permanently">
              <Trash2 size={11} />
            </button>
          </div>
        )}
      </div>
    </BrioListItem>
  );
}

// ── Domain Group ─────────────────────────────────────────────────────────────

function DomainGroup({ domain, items, fmtCurr, selectedIds, hasSelection, onSelect,
                        onAcknowledge, onSnooze, onResolve, onDismiss, onNavigate }) {
  const [expanded, setExpanded] = useState(true);
  if (!items || items.length === 0) return null;

  const Icon = DOMAIN_ICONS[domain] || Activity;
  const critCount = items.filter(i => i.severity === 'critical').length;

  return (
    <div>
      {/* Domain header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 12px', background: 'var(--brio-grey-light)',
          cursor: 'pointer', fontFamily: font, userSelect: 'none',
        }}
      >
        {expanded ? <ChevronDown size={12} color="var(--brio-grey)" /> : <ChevronRight size={12} color="var(--brio-grey)" />}
        <Icon size={12} style={{ color: 'var(--brio-grey)' }} />
        <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--brio-black)' }}>
          {DOMAIN_LABELS[domain]}
        </span>
        <span style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)' }}>
          {items.length}
        </span>
        {critCount > 0 && (
          <BrioBadge variant="danger">{critCount} critical</BrioBadge>
        )}
      </div>

      {/* Items */}
      {expanded && (
        <BrioListWrapper gap="xs">
          {items.map(item => (
            <RadarItemRow
              key={item.id}
              item={item}
              fmtCurr={fmtCurr}
              selected={selectedIds.has(item.id)}
              hasSelection={hasSelection}
              onSelect={onSelect}
              onAcknowledge={onAcknowledge}
              onSnooze={onSnooze}
              onResolve={onResolve}
              onDismiss={onDismiss}
              onNavigate={onNavigate}
            />
          ))}
        </BrioListWrapper>
      )}
    </div>
  );
}

// Domains shown in the main radar body (excludes system — shown in separate drawer)
const MAIN_DOMAINS = ['finance', 'projects', 'bd', 'hiring', 'team'];

// ── Time Bucket ──────────────────────────────────────────────────────────────

function TimeBucket({ bucketKey, domainItems, fmtCurr, collapsed, onToggle,
                       selectedIds, hasSelection, onSelect, onAcknowledge, onSnooze, onResolve, onDismiss, onNavigate }) {
  const totalItems = MAIN_DOMAINS.reduce((sum, d) => sum + (domainItems[d]?.length || 0), 0);
  if (totalItems === 0) return null;

  return (
    <BrioAnalyticsSurface padding="xs" className="brio-mb-s">
      {/* Bucket header */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 12px', cursor: 'pointer', userSelect: 'none',
        }}
      >
        {collapsed
          ? <ChevronRight size={14} color="var(--brio-black)" />
          : <ChevronDown size={14} color="var(--brio-black)" />
        }
        <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--brio-black)', fontFamily: font }}>
          {BUCKET_LABELS[bucketKey]}
        </span>
        <span style={{ fontSize: 11, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>
          {totalItems} {totalItems === 1 ? 'item' : 'items'}
        </span>
      </div>

      {/* Domain groups (main domains only — system/data items in separate drawer) */}
      {!collapsed && MAIN_DOMAINS.map(domain => (
        <DomainGroup
          key={domain}
          domain={domain}
          items={domainItems[domain]}
          fmtCurr={fmtCurr}
          selectedIds={selectedIds}
          hasSelection={hasSelection}
          onSelect={onSelect}
          onAcknowledge={onAcknowledge}
          onSnooze={onSnooze}
          onResolve={onResolve}
          onDismiss={onDismiss}
          onNavigate={onNavigate}
        />
      ))}
    </BrioAnalyticsSurface>
  );
}

// ── Data Completeness Drawer ─────────────────────────────────────────────────
// Separated from main radar to avoid clutter. Shows all system/data items in
// a collapsible panel at the bottom of the page.

function DataCompletenessDrawer({ items, fmtCurr, selectedIds, hasSelection, onSelect,
                                   onAcknowledge, onSnooze, onResolve, onDismiss, onNavigate }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;

  return (
    <BrioAnalyticsSurface padding="xs" className="brio-mt-s">
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 12px', cursor: 'pointer', userSelect: 'none',
        }}
      >
        {open
          ? <ChevronDown size={14} color="var(--brio-grey)" />
          : <ChevronRight size={14} color="var(--brio-grey)" />
        }
        <Database size={13} style={{ color: 'var(--brio-grey)' }} />
        <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--brio-grey)', fontFamily: font }}>
          Data Completeness
        </span>
        <span style={{
          fontSize: 10, fontWeight: 400, color: 'var(--brio-gold)',
          background: 'var(--brio-gold-light)', padding: '1px 7px',
          fontFamily: font,
        }}>
          {items.length}
        </span>
        {!open && (
          <span style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', fontStyle: 'italic', fontFamily: font }}>
            Missing fields across projects, clients, team members, and setup
          </span>
        )}
      </div>

      {open && (
        <BrioListWrapper gap="xs">
          {items.map(item => (
            <RadarItemRow
              key={item.id}
              item={item}
              fmtCurr={fmtCurr}
              selected={selectedIds.has(item.id)}
              hasSelection={hasSelection}
              onSelect={onSelect}
              onAcknowledge={onAcknowledge}
              onSnooze={onSnooze}
              onResolve={onResolve}
              onDismiss={onDismiss}
              onNavigate={onNavigate}
            />
          ))}
        </BrioListWrapper>
      )}
    </BrioAnalyticsSurface>
  );
}

// ── Filter Bar ───────────────────────────────────────────────────────────────

// ── Monthly Archive & CA Workflow Panel ───────────────────────────────────────
// Company-wide workflow: archive previous month's tax invoices, email CA, mark sent.
// Moved from InvoicesPage to Radar for company-wide (not per-project) visibility.

function MonthlyArchivePanel({ invoices, fmtCurr, currentUser, projects, invoicesHook, radarSettingsHook, onComplete }) {
  const [spStatus, setSpStatus] = useState({ configured: false });
  const [archiving, setArchiving] = useState(false);
  const [archiveProgress, setArchiveProgress] = useState(null);
  const [archiveResult, setArchiveResult] = useState(null);
  const [markingSent, setMarkingSent] = useState(false);
  const [markedSent, setMarkedSent] = useState(false);

  // Previous month key
  const prevMonthKey = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const monthLabel = useMemo(() => {
    return new Date(prevMonthKey + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }, [prevMonthKey]);

  // Check SharePoint config on mount
  useEffect(() => {
    isSharePointConfigured().then(setSpStatus);
  }, []);

  // Tax invoices for previous month (issued or paid)
  const monthInvoices = useMemo(() => {
    if (!invoices?.length) return [];
    return invoices.filter(inv => {
      const dt = inv.invoiceDate || '';
      const month = dt.slice(0, 7);
      const docType = inv.invoiceDocType || 'tax';
      const status = inv.status || '';
      return month === prevMonthKey
        && docType === 'tax'
        && (status === 'issued' || status === 'paid');
    });
  }, [invoices, prevMonthKey]);

  const alreadyArchived = monthInvoices.filter(i => i.archivedToStorage);
  const alreadySent = monthInvoices.filter(i => i.sentToAccountant);
  const notYetArchived = monthInvoices.filter(i => !i.archivedToStorage);
  const notYetSent = monthInvoices.filter(i => !i.sentToAccountant);
  const totalNet = monthInvoices.reduce((s, i) => s + (Number(i.netAmount) || 0), 0);

  const projectsMap = useMemo(() =>
    Object.fromEntries((projects || []).map(p => [p.id, p])),
    [projects]
  );

  // Generate PDF for a single invoice
  const generatePdf = useCallback(async (invoiceId, officeKey, docType) => {
    try {
      const { supabase } = await import('../../supabaseClient');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || '';
      const res = await fetch('/api/invoice-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ invoiceId, officeKey, invoiceDocType: docType }),
      });
      if (!res.ok) return null;
      return await res.blob();
    } catch {
      return null;
    }
  }, []);

  // Archive workflow
  const handleArchive = useCallback(async () => {
    if (!notYetArchived.length) return;
    setArchiving(true);
    setArchiveResult(null);
    setArchiveProgress({ current: 0, total: notYetArchived.length, label: '', status: '' });

    const result = await archiveMonthlyTaxInvoices({
      invoices: notYetArchived,
      generatePdf,
      onProgress: (idx, total, label, status) => {
        setArchiveProgress({ current: idx + 1, total, label, status });
      },
      projectsMap,
      uploadToSharePoint: spStatus.configured,
      archivedByUserId: currentUser?.id,
    });

    setArchiveResult(result);
    setArchiving(false);
    invoicesHook?.refreshInvoices?.();
  }, [notYetArchived, generatePdf, projectsMap, spStatus.configured, currentUser?.id, invoicesHook]);

  // Mark as sent to accountant
  const handleMarkSentToAccountant = useCallback(async () => {
    if (!notYetSent.length) return;
    setMarkingSent(true);
    const ids = notYetSent.map(i => i.id);
    const result = await markSentToAccountant(ids, currentUser?.id);
    setMarkingSent(false);
    if (result.success) {
      setMarkedSent(true);
      invoicesHook?.refreshInvoices?.();
    }
  }, [notYetSent, currentUser?.id, invoicesHook]);

  // Prepare accountant email
  const handlePrepareAccountantEmail = useCallback(() => {
    const { subject, body } = buildAccountantEmailSummary(monthInvoices, prevMonthKey, fmtCurr);
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailto, '_blank');
  }, [monthInvoices, prevMonthKey, fmtCurr]);

  // Mark the radar item as resolved when workflow is complete
  const allDone = monthInvoices.length > 0
    && alreadyArchived.length === monthInvoices.length
    && (alreadySent.length === monthInvoices.length || markedSent);

  useEffect(() => {
    if (allDone && radarSettingsHook) {
      radarSettingsHook.resolveItem('ca_monthly_reminder', 'system', `ca-${prevMonthKey}`);
      onComplete?.();
    }
  }, [allDone, radarSettingsHook, prevMonthKey, onComplete]);

  const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div>
      <div style={{ padding: '4px 0' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          fontSize: 12, fontWeight: 400, color: 'var(--brio-black)', fontFamily: font,
        }}>
          <Archive size={14} style={{ color: 'var(--brio-gold)' }} />
          Monthly Archive &amp; Accountant Workflow — {monthLabel}
        </div>

        {/* Summary */}
        <div style={{
          padding: '8px 12px', background: 'var(--brio-grey-light)',
          border: '1px solid var(--brio-border)', marginBottom: 12, fontFamily: font,
        }}>
          <div style={{ fontWeight: 400, fontSize: 11, marginBottom: 4 }}>
            Tax Invoices — {monthLabel}
          </div>
          <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)' }}>
            {monthInvoices.length === 0
              ? 'No issued/paid tax invoices found for this month.'
              : <>
                  {monthInvoices.length} invoice{monthInvoices.length !== 1 ? 's' : ''} — Total net: {fmtCurr ? fmtCurr(totalNet) : totalNet}
                  {alreadyArchived.length > 0 && <> — {alreadyArchived.length} archived</>}
                  {alreadySent.length > 0 && <> — {alreadySent.length} sent to CA</>}
                </>}
          </div>
        </div>

        {monthInvoices.length > 0 && (
          <>
            {/* Invoice list */}
            <div style={{ marginBottom: 12, maxHeight: 200, overflowY: 'auto' }}>
              <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse', fontFamily: font }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--brio-border)', textAlign: 'left' }}>
                    <th style={{ padding: '3px 6px', fontWeight: 400 }}>Invoice #</th>
                    <th style={{ padding: '3px 6px', fontWeight: 400 }}>Date</th>
                    <th style={{ padding: '3px 6px', fontWeight: 400, textAlign: 'right' }}>Net Amount</th>
                    <th style={{ padding: '3px 6px', fontWeight: 400 }}>Status</th>
                    <th style={{ padding: '3px 6px', fontWeight: 400 }}>Archived</th>
                    <th style={{ padding: '3px 6px', fontWeight: 400 }}>Sent to CA</th>
                  </tr>
                </thead>
                <tbody>
                  {monthInvoices.map(inv => (
                    <tr key={inv.id} style={{ borderBottom: '1px solid var(--brio-border)' }}>
                      <td style={{ padding: '3px 6px', fontWeight: 300 }}>{inv.invoiceNumber || `#${inv.id}`}</td>
                      <td style={{ padding: '3px 6px', fontWeight: 300 }}>{fmtDate(inv.invoiceDate)}</td>
                      <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 300 }}>{fmtCurr ? fmtCurr(inv.netAmount) : inv.netAmount}</td>
                      <td style={{ padding: '3px 6px' }}>
                        <BrioBadge variant={inv.status === 'paid' ? 'success' : 'neutral'}>{inv.status}</BrioBadge>
                      </td>
                      <td style={{ padding: '3px 6px' }}>
                        {inv.archivedToStorage
                          ? <CheckCircle size={11} style={{ color: 'var(--brio-green, #16a34a)' }} />
                          : <span style={{ color: 'var(--brio-grey)' }}>—</span>}
                      </td>
                      <td style={{ padding: '3px 6px' }}>
                        {inv.sentToAccountant
                          ? <CheckCircle size={11} style={{ color: 'var(--brio-green, #16a34a)' }} />
                          : <span style={{ color: 'var(--brio-grey)' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Archive */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={handleArchive}
                  disabled={archiving || notYetArchived.length === 0}
                  style={{
                    ...actionBtnGold,
                    opacity: archiving || notYetArchived.length === 0 ? 0.5 : 1,
                    cursor: archiving || notYetArchived.length === 0 ? 'not-allowed' : 'pointer',
                  }}>
                  {archiving
                    ? <><Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> Archiving…</>
                    : <><FolderOpen size={11} /> Archive {notYetArchived.length} Invoice{notYetArchived.length !== 1 ? 's' : ''}</>}
                </button>
                <span style={{ fontSize: 9, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>
                  {spStatus.configured
                    ? `Upload to ${spStatus.siteName || 'SharePoint'} + mark archived`
                    : 'Generate PDFs + mark archived (SharePoint not configured)'}
                </span>
              </div>

              {/* Progress */}
              {archiveProgress && archiving && (
                <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>
                  {archiveProgress.current}/{archiveProgress.total} — {archiveProgress.label}
                  {archiveProgress.status === 'generating' && ' (generating PDF…)'}
                  {archiveProgress.status === 'uploading' && ' (uploading…)'}
                </div>
              )}

              {/* Archive result */}
              {archiveResult && !archiving && (
                <div style={{
                  padding: '6px 10px', fontSize: 10, fontFamily: font, fontWeight: 300,
                  background: archiveResult.failed > 0 ? '#fef2f2' : '#f0fdf4',
                  border: `1px solid ${archiveResult.failed > 0 ? '#fca5a5' : '#86efac'}`,
                }}>
                  Archived: {archiveResult.archived} — Failed: {archiveResult.failed}
                  {archiveResult.errors?.length > 0 && (
                    <div style={{ marginTop: 4, color: 'var(--brio-red)' }}>
                      {archiveResult.errors.map((e, i) => <div key={i}>{e}</div>)}
                    </div>
                  )}
                </div>
              )}

              {/* Prepare accountant email */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={handlePrepareAccountantEmail}
                  disabled={monthInvoices.length === 0}
                  style={{
                    ...actionBtn,
                    opacity: monthInvoices.length === 0 ? 0.5 : 1,
                    cursor: monthInvoices.length === 0 ? 'not-allowed' : 'pointer',
                  }}>
                  <Mail size={11} /> Prepare Accountant Email
                </button>
                <span style={{ fontSize: 9, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>
                  Opens email with invoice summary — attach PDFs manually
                </span>
              </div>

              {/* Mark as sent */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={handleMarkSentToAccountant}
                  disabled={markingSent || notYetSent.length === 0 || markedSent}
                  style={{
                    ...actionBtn,
                    opacity: markingSent || notYetSent.length === 0 || markedSent ? 0.5 : 1,
                    cursor: markingSent || notYetSent.length === 0 || markedSent ? 'not-allowed' : 'pointer',
                  }}>
                  {markedSent
                    ? <><CheckCircle size={11} style={{ color: 'var(--brio-green, #16a34a)' }} /> Marked as Sent</>
                    : markingSent
                      ? <><Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> Marking…</>
                      : <><Check size={11} /> Mark {notYetSent.length} as Sent to CA</>}
                </button>
              </div>
            </div>

            <div style={{ fontSize: 9, fontWeight: 300, color: 'var(--brio-grey)', fontStyle: 'italic', marginTop: 8, fontFamily: font }}>
              Proforma invoices are excluded. Tax invoices are the primary accounting archive target.
            </div>
          </>
        )}

        {/* No invoices — allow resolving the radar item manually */}
        {monthInvoices.length === 0 && (
          <button
            onClick={() => {
              radarSettingsHook?.resolveItem('ca_monthly_reminder', 'system', `ca-${prevMonthKey}`);
              onComplete?.();
            }}
            style={actionBtn}>
            <Check size={11} /> Nothing to send — mark complete
          </button>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Filter Bar ───────────────────────────────────────────────────────────────

function FilterBar({ filters, setFilters, projects }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', padding: '8px 0' }}>
      {/* Severity */}
      {['critical', 'warning', 'signal'].map(sev => (
        <button key={sev} style={filterBtn(filters.severity === sev)}
          onClick={() => setFilters(f => ({ ...f, severity: f.severity === sev ? null : sev }))}>
          {SEVERITY_MAP[sev].label}
        </button>
      ))}

      <span style={{ width: 1, height: 16, background: 'var(--brio-border)', margin: '0 4px' }} />

      {/* Domain */}
      {Object.entries(DOMAIN_LABELS).map(([key, label]) => (
        <button key={key} style={filterBtn(filters.domain === key)}
          onClick={() => setFilters(f => ({ ...f, domain: f.domain === key ? null : key }))}>
          {label}
        </button>
      ))}

      <span style={{ width: 1, height: 16, background: 'var(--brio-border)', margin: '0 4px' }} />

      {/* Status */}
      {['open', 'acknowledged', 'snoozed', 'resolved', 'dismissed'].map(st => (
        <button key={st} style={filterBtn(filters.status === st)}
          onClick={() => setFilters(f => ({ ...f, status: f.status === st ? null : st }))}>
          {STATUS_MAP[st].label}
        </button>
      ))}

      <span style={{ width: 1, height: 16, background: 'var(--brio-border)', margin: '0 4px' }} />

      {/* Silent failures */}
      <button style={filterBtn(filters.silentFailureOnly)}
        onClick={() => setFilters(f => ({ ...f, silentFailureOnly: !f.silentFailureOnly }))}>
        <EyeOff size={10} /> Silent failures
      </button>

      {/* Project */}
      {projects && projects.length > 0 && (
        <select
          value={filters.projectId || ''}
          onChange={(e) => setFilters(f => ({ ...f, projectId: e.target.value || null }))}
          style={{
            padding: '3px 8px', border: '1px solid var(--brio-border)',
            fontSize: 11, fontWeight: 300, color: 'var(--brio-grey)',
            background: 'transparent', fontFamily: font, cursor: 'pointer',
          }}
        >
          <option value="">All projects</option>
          {[...projects].sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || '')).map(p => <option key={p.id} value={p.id}>{p.projectCode ? `${p.projectCode} \u2014 ${p.name}` : p.name}</option>)}
        </select>
      )}

      {/* Clear */}
      {(filters.severity || filters.domain || filters.status || filters.silentFailureOnly || filters.projectId) && (
        <button style={{ ...filterBtn(false), color: 'var(--brio-red)' }}
          onClick={() => setFilters({ severity: null, domain: null, status: null, silentFailureOnly: false, projectId: null })}>
          <X size={10} /> Clear
        </button>
      )}
    </div>
  );
}

// ── Bulk Action Bar (shown when items are selected) ──────────────────────────

function BulkActionBar({ count, onAction, onClear }) {
  if (count === 0) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
      background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)',
      marginBottom: 8, position: 'sticky', top: 0, zIndex: 10,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}>
      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--brio-black)', fontFamily: font }}>
        {count} selected
      </span>
      <span style={{ width: 1, height: 16, background: 'var(--brio-border)' }} />
      <button style={actionBtnGold} onClick={() => onAction('acknowledge')}>
        <Eye size={11} /> Acknowledge
      </button>
      <button style={actionBtn} onClick={() => onAction('snooze')}>
        <Clock size={11} /> Snooze
      </button>
      <button style={actionBtn} onClick={() => onAction('resolve')}>
        <Check size={11} /> Resolve
      </button>
      <button style={{ ...actionBtn, color: 'var(--brio-red)', borderColor: 'var(--brio-red)' }} onClick={() => onAction('dismiss')}>
        <Trash2 size={11} /> Dismiss
      </button>
      <div style={{ flex: 1 }} />
      <button style={actionBtn} onClick={onClear}>
        <X size={11} /> Deselect all
      </button>
    </div>
  );
}

// ── Main Page Component ──────────────────────────────────────────────────────

export default function RadarDashboardPage({
  projects = [],
  clients = [],
  users = [],
  invoices = [],
  timeEntries = [],
  userLastEntryDates = {},
  userCurrentMonthHours = {},
  timeOff = [],
  inquiries = [],
  candidates = [],
  interviews = [],
  radarSettingsHook = null,
  invoicesHook = null,
  currentUser = null,
  isAdmin = false,
  fmtCurr = null,
  customEmailTemplates = null,
  onNavigate = null,
  onRefreshRadarData = null,
}) {
  const [refreshing, setRefreshing] = useState(false);
  // handleRefresh is defined before the data hooks below, so it reaches their
  // refreshers through a ref rather than closing over bindings that do not exist
  // yet at this point in the component body.
  const refreshPressFolderAlertsRef = useRef(null);
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([
        radarSettingsHook?.refresh?.(),
        onRefreshRadarData?.(),
        // Dropbox drift arrives between renders — a folder moved seconds ago is
        // the case this page exists for, and without this the button that looks
        // like it re-reads everything silently keeps the list from mount time.
        refreshPressFolderAlertsRef.current?.(),
      ]);
    } finally {
      setRefreshing(false);
    }
  };
  // Fetch all invoices (not project-scoped) when Radar mounts
  useEffect(() => {
    if (invoicesHook?.fetchAllInvoices) {
      invoicesHook.fetchAllInvoices();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [filters, setFilters] = useState({
    severity: null, domain: null, status: null,
    silentFailureOnly: false, projectId: null,
  });
  const [collapsedBuckets, setCollapsedBuckets] = useState({
    today: false, this_week: false, upcoming: true,
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDismiss, setConfirmDismiss] = useState(null); // holds items to dismiss
  const [actionError, setActionError] = useState(null); // error message for failed actions
  const lastSelectedRef = useRef(null); // tracks last clicked item id for shift-select

  const radarSettings = radarSettingsHook?.settings || {};
  const itemStatuses = radarSettingsHook?.itemStatuses || [];

  const currentWeekId = useMemo(() => getCurrentISOWeekId(), []);
  const { assignments: weeklyAssignments } = useHFBTeamAssignments(currentWeekId);

  // Compute radar data
  // Open data-integrity findings, written nightly by check_stage_date_integrity()
  const { findings: integrityFindings } = useDataIntegrityFindings();
  // Dropbox folder drift, written by the dropbox-webhook edge function.
  const { alerts: pressFolderAlerts, refresh: refreshPressFolderAlerts } = usePressFolderAlerts();
  refreshPressFolderAlertsRef.current = refreshPressFolderAlerts;

  const { items, summary, grouped } = useRadar({
    projects, clients, users, invoices, timeEntries, userLastEntryDates, userCurrentMonthHours,
    timeOff, inquiries, candidates, interviews, radarSettings, itemStatuses, currentUser, fmtCurr,
    weeklyAssignments, integrityFindings, pressFolderAlerts,
  });

  // Filter grouped data
  const filteredGrouped = useMemo(() => {
    const result = {};
    for (const [bucket, domains] of Object.entries(grouped)) {
      result[bucket] = {};
      for (const [domain, domainItems] of Object.entries(domains)) {
        let filtered = [...domainItems];

        if (filters.severity) filtered = filtered.filter(i => i.severity === filters.severity);
        if (filters.domain) filtered = filtered.filter(i => i.domain === filters.domain);
        if (filters.status) {
          filtered = filtered.filter(i => i.status === filters.status);
        } else {
          // By default hide snoozed and dismissed
          filtered = filtered.filter(i => i.status !== 'snoozed' && i.status !== 'dismissed');
        }
        if (filters.silentFailureOnly) filtered = filtered.filter(i => i.is_silent_failure);
        if (filters.projectId) filtered = filtered.filter(i => String(i.project_id) === String(filters.projectId));

        filtered.sort((a, b) => {
          const sevOrder = { critical: 0, warning: 1, signal: 2 };
          const diff = (sevOrder[a.severity] || 2) - (sevOrder[b.severity] || 2);
          if (diff !== 0) return diff;
          return (b.days_overdue || 0) - (a.days_overdue || 0);
        });
        result[bucket][domain] = filtered;
      }
    }
    return result;
  }, [grouped, filters]);

  // Separate system/data-completeness items into their own list for the drawer
  const dataCompletenessItems = useMemo(() => {
    const all = [];
    for (const domains of Object.values(filteredGrouped)) {
      if (domains.system) all.push(...domains.system);
    }
    return all;
  }, [filteredGrouped]);

  // Collect all visible items for "select all" logic (main + drawer)
  const allVisibleItems = useMemo(() => {
    const all = [];
    for (const domains of Object.values(filteredGrouped)) {
      for (const [domain, domainItems] of Object.entries(domains)) {
        if (domain === 'system') continue; // system items are in drawer, still included for selection
        all.push(...domainItems);
      }
    }
    // Append data completeness items so shift-select and select-all include them
    all.push(...dataCompletenessItems);
    return all;
  }, [filteredGrouped, dataCompletenessItems]);

  // Selection handlers — supports shift-click for range selection
  const handleSelect = useCallback((item, event) => {
    const isShift = event?.shiftKey;

    if (isShift && lastSelectedRef.current != null) {
      // Shift-click: select all items between lastSelected and this item
      const lastIdx = allVisibleItems.findIndex(i => i.id === lastSelectedRef.current);
      const curIdx = allVisibleItems.findIndex(i => i.id === item.id);
      if (lastIdx !== -1 && curIdx !== -1) {
        const start = Math.min(lastIdx, curIdx);
        const end = Math.max(lastIdx, curIdx);
        setSelectedIds(prev => {
          const next = new Set(prev);
          for (let i = start; i <= end; i++) {
            next.add(allVisibleItems[i].id);
          }
          return next;
        });
        lastSelectedRef.current = item.id;
        return;
      }
    }

    // Normal click: toggle single item
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
    lastSelectedRef.current = item.id;
  }, [allVisibleItems]);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === allVisibleItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allVisibleItems.map(i => i.id)));
    }
  }, [selectedIds.size, allVisibleItems]);

  // Helper to run an action and surface errors
  const runAction = useCallback(async (fn) => {
    setActionError(null);
    const result = await fn;
    if (result && !result.success) {
      setActionError(result.error || 'Action failed. Please try again.');
    }
    return result;
  }, []);

  // Single-item action handlers
  const handleAcknowledge = useCallback(async (item) => {
    await runAction(radarSettingsHook?.acknowledgeItem(item.type, item.entity_type, item.entity_id));
  }, [radarSettingsHook, runAction]);

  const handleSnooze = useCallback(async (item) => {
    await runAction(radarSettingsHook?.snoozeItem(item.type, item.entity_type, item.entity_id, 7));
  }, [radarSettingsHook, runAction]);

  const handleResolve = useCallback(async (item) => {
    await runAction(radarSettingsHook?.resolveItem(item.type, item.entity_type, item.entity_id));
  }, [radarSettingsHook, runAction]);

  const handleDismiss = useCallback((item) => {
    setActionError(null);
    setConfirmDismiss([item]);
  }, []);

  const confirmDismissAction = useCallback(async () => {
    if (!confirmDismiss || confirmDismiss.length === 0) return;
    setActionError(null);
    let result;
    if (confirmDismiss.length === 1) {
      result = await radarSettingsHook?.dismissItem(confirmDismiss[0].type, confirmDismiss[0].entity_type, confirmDismiss[0].entity_id);
    } else {
      result = await radarSettingsHook?.bulkAction(confirmDismiss, 'dismiss');
    }
    if (result && !result.success) {
      setActionError(result.error || 'Dismiss failed. Please try again.');
      setConfirmDismiss(null);
      return;
    }
    setConfirmDismiss(null);
    setSelectedIds(new Set());
  }, [confirmDismiss, radarSettingsHook]);

  // Bulk action handler
  const handleBulkAction = useCallback(async (action) => {
    const selected = allVisibleItems.filter(i => selectedIds.has(i.id));
    if (selected.length === 0) return;

    if (action === 'dismiss') {
      setConfirmDismiss(selected);
      return;
    }

    const result = await radarSettingsHook?.bulkAction(selected, action);
    if (result && !result.success) {
      setActionError(result.error || 'Action failed. Please try again.');
      return;
    }
    setActionError(null);
    setSelectedIds(new Set());
  }, [selectedIds, allVisibleItems, radarSettingsHook]);

  const handleNavigate = useCallback((item) => {
    // CA monthly reminder opens the archive workflow modal
    if (item.type === 'ca_monthly_reminder') {
      setArchivePanelOpen(true);
      return;
    }
    if (onNavigate && item.navigation_target) {
      onNavigate(item.navigation_target, item.navigation_params);
    }
  }, [onNavigate]);

  const toggleBucket = useCallback((bucket) => {
    setCollapsedBuckets(prev => ({ ...prev, [bucket]: !prev[bucket] }));
  }, []);

  const visibleCount = allVisibleItems.length;
  const hasSelection = selectedIds.size > 0;

  // Detect active CA monthly reminder item (for showing archive panel)
  const caReminderItem = useMemo(() => {
    return allVisibleItems.find(i => i.type === 'ca_monthly_reminder' && i.status !== 'resolved' && i.status !== 'dismissed');
  }, [allVisibleItems]);

  // Invoice pipeline items (proforma + tax invoices pending)
  const invoicePipelineItems = useMemo(() => {
    return items.filter(i =>
      (i.type === 'invoice_proforma_to_send' || i.type === 'invoice_tax_to_send') &&
      i.status !== 'resolved' && i.status !== 'dismissed'
    ).sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
  }, [items]);

  // Hiring pipeline items (admin actions requested by supervisors: offer/hire)
  const hiringPipelineItems = useMemo(() => {
    return items.filter(i =>
      i.type === 'hiring_admin_action' &&
      i.status !== 'resolved' && i.status !== 'dismissed'
    ).sort((a, b) => {
      if (a.severity !== b.severity) return a.severity === 'critical' ? -1 : 1;
      return (b.days_overdue || 0) - (a.days_overdue || 0);
    });
  }, [items]);

  // Client birthday/anniversary items for mail workflow
  const clientEventItems = useMemo(() => {
    return items.filter(i =>
      (i.type === 'client_birthday' || i.type === 'client_anniversary') &&
      i.status !== 'resolved' && i.status !== 'dismissed'
    ).sort((a, b) => {
      if (a.severity !== b.severity) return a.severity === 'critical' ? -1 : 1;
      return (a.due_date || '').localeCompare(b.due_date || '');
    });
  }, [items]);

  // Send client wishes email
  const handleSendClientWishes = useCallback((item) => {
    const templateType = item.type; // 'client_birthday' or 'client_anniversary'
    const template = resolveTemplate(templateType, customEmailTemplates);
    const clientName = item.meta?.clientName || item.entity_label || '';
    const subject = (template.subject || '').replace(/\{clientName\}/g, clientName);
    const body = (template.body || '').replace(/\{clientName\}/g, clientName);
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailto, '_blank');
  }, [customEmailTemplates]);

  const [archivePanelOpen, setArchivePanelOpen] = useState(false);

  return (
    <>
      {/* Page header — full-width BRIO band */}
      <BrioPageHeader
        title="Radar"
        subtitle="Practice operations at a glance"
        right={
          <button
            style={{ ...actionBtn, opacity: refreshing ? 0.6 : 1 }}
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh"
          >
            <RefreshCw size={12} className={refreshing ? 'brio-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      <BrioFrame>
        {/* ── Summary row ───────────────────────────────────────────── */}
        <BrioAnalyticsSurface padding="s" className="brio-mb-s">
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            {/* Health score */}
            <div style={{ minWidth: 200 }}>
              <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, fontFamily: font }}>
                Practice Health
              </div>
              <ScoreBlock score={summary.score} label={summary.label} />
            </div>

            <div style={{ width: 1, height: 32, background: 'var(--brio-border)' }} />

            {/* Metric counts */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {summary.criticalCount > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 400, color: 'var(--brio-red)', fontFamily: font }}>{summary.criticalCount}</div>
                  <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>critical</div>
                </div>
              )}
              {summary.warningCount > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 400, color: 'var(--brio-gold)', fontFamily: font }}>{summary.warningCount}</div>
                  <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>warnings</div>
                </div>
              )}
              {summary.signalCount > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 400, color: 'var(--brio-teal)', fontFamily: font }}>{summary.signalCount}</div>
                  <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>signals</div>
                </div>
              )}
              {summary.silentFailureCount > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 400, color: 'var(--brio-orange)', fontFamily: font }}>{summary.silentFailureCount}</div>
                  <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>silent failures</div>
                </div>
              )}
            </div>
          </div>
        </BrioAnalyticsSurface>

        {/* ── Drift indicator ─────────────────────────────────────── */}
        <BrioAnalyticsSurface padding="xs" className="brio-mb-s">
          <div style={{ padding: '4px 8px' }}>
            <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, fontFamily: font }}>
              Practice Drift
            </div>
            <DriftRow domainHealth={summary.domainHealth} />
          </div>
        </BrioAnalyticsSurface>

        {/* ── Primary Risk (admin only) ───────────────────────────── */}
        {isAdmin && summary.primaryRisk && (
          <div style={{ marginBottom: 'var(--brio-space-s)' }}>
            <PrimaryRiskBlock item={summary.primaryRisk} fmtCurr={fmtCurr} />
          </div>
        )}

        {/* ── Invoice Pipeline (dedicated section) ──────────────────── */}
        {isAdmin && invoicePipelineItems.length > 0 && (
          <BrioAnalyticsSurface padding="xs" className="brio-mb-s">
            <div style={{ padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <FileText size={16} style={{ color: 'var(--brio-gold)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--brio-black)', fontFamily: font }}>
                    Invoice Pipeline
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>
                    {invoicePipelineItems.length} invoice{invoicePipelineItems.length !== 1 ? 's' : ''} pending
                  </div>
                </div>
                <BrioBadge variant="danger">{invoicePipelineItems.length} pending</BrioBadge>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {invoicePipelineItems.map(item => (
                  <div
                    key={item.id}
                    onClick={() => handleNavigate(item)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 10px', cursor: 'pointer',
                      background: item.type === 'invoice_proforma_to_send' ? 'var(--brio-gold-light, rgba(159,144,42,0.08))' : 'var(--brio-teal-light, rgba(104,149,129,0.08))',
                      border: '1px solid var(--brio-border)',
                    }}
                  >
                    <FileText size={12} style={{ color: item.type === 'invoice_proforma_to_send' ? 'var(--brio-gold)' : 'var(--brio-teal)', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 400, fontFamily: font }}>
                        {item.meta?.projectName || item.entity_label || '—'}
                        <span style={{ color: 'var(--brio-grey)', marginLeft: 6, fontSize: 10 }}>
                          {item.meta?.stageKey || ''}
                        </span>
                      </div>
                      <div style={{ fontSize: 9, color: 'var(--brio-grey)', fontFamily: font }}>
                        {item.type === 'invoice_proforma_to_send' ? 'Proforma' : 'Tax'} — due {item.due_date || '—'}
                      </div>
                    </div>
                    <BrioBadge variant={item.severity === 'critical' ? 'danger' : 'warning'}>
                      {item.type === 'invoice_proforma_to_send' ? 'Proforma' : 'Tax'}
                    </BrioBadge>
                    <button
                      style={{
                        ...actionBtnGold,
                        padding: '4px 12px',
                        fontSize: 10,
                      }}
                      onClick={(e) => { e.stopPropagation(); handleNavigate(item); }}
                    >
                      <Mail size={10} /> Open Pipeline
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </BrioAnalyticsSurface>
        )}

        {/* ── Hiring Pipeline (dedicated section — always visible for admin) ── */}
        {isAdmin && (
          <BrioAnalyticsSurface padding="xs" className="brio-mb-s">
            <div style={{ padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: hiringPipelineItems.length > 0 ? 8 : 0 }}>
                <UserPlus size={16} style={{ color: 'var(--brio-gold)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--brio-black)', fontFamily: font }}>
                    Hiring Pipeline
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>
                    {hiringPipelineItems.length > 0
                      ? `${hiringPipelineItems.length} item${hiringPipelineItems.length !== 1 ? 's' : ''} requiring attention`
                      : `No pending hiring actions · ${candidates.filter(c => !!c.pendingAdminAction).length} candidate(s) with pending action in data`
                    }
                  </div>
                </div>
                {hiringPipelineItems.length > 0 && (
                  <BrioBadge variant={hiringPipelineItems.some(i => i.severity === 'critical') ? 'danger' : 'warning'}>
                    {hiringPipelineItems.length} pending
                  </BrioBadge>
                )}
              </div>
              {hiringPipelineItems.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {hiringPipelineItems.map(item => (
                    <div
                      key={item.id}
                      onClick={() => handleNavigate(item)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 10px', cursor: 'pointer',
                        background: item.meta?.actionType === 'offer'
                          ? 'var(--brio-gold-light, rgba(159,144,42,0.08))'
                          : item.meta?.actionType === 'send_hire_letter'
                            ? 'var(--brio-blue-light, rgba(66,133,244,0.08))'
                            : 'var(--brio-teal-light, rgba(104,149,129,0.08))',
                        border: '1px solid var(--brio-border)',
                      }}
                    >
                      <UserPlus size={12} style={{
                        color: item.meta?.actionType === 'offer' ? 'var(--brio-gold)' : 'var(--brio-teal)',
                        flexShrink: 0,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 400, fontFamily: font }}>
                          {item.meta?.candidateName || item.entity_label || '—'}
                          <span style={{ color: 'var(--brio-grey)', marginLeft: 6, fontSize: 10 }}>
                            {item.meta?.jobRole || ''}
                          </span>
                        </div>
                        <div style={{ fontSize: 9, color: 'var(--brio-grey)', fontFamily: font }}>
                          {item.title}{item.days_overdue > 0 ? ` — ${item.days_overdue}d overdue` : ''}
                        </div>
                      </div>
                      <BrioBadge variant={item.severity === 'critical' ? 'danger' : 'warning'}>
                        {item.meta?.actionType === 'offer' ? 'Offer' : item.meta?.actionType === 'send_hire_letter' ? 'Letter' : 'Hire'}
                      </BrioBadge>
                      <button
                        style={{
                          ...actionBtnGold,
                          padding: '4px 12px',
                          fontSize: 10,
                        }}
                        onClick={(e) => { e.stopPropagation(); handleNavigate(item); }}
                      >
                        <UserPlus size={10} /> View Candidate
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  marginTop: 8, padding: '8px 10px',
                  background: 'var(--brio-bg-light, #f9f9f6)',
                  border: '1px dashed var(--brio-border)',
                  fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font,
                  textAlign: 'center',
                }}>
                  No hiring actions pending. When a candidate is hired, a notification to send a confirmation letter will appear here.
                </div>
              )}
            </div>
          </BrioAnalyticsSurface>
        )}

        {/* ── Client Birthdays & Anniversaries Mail Workflow ──────── */}
        {clientEventItems.length > 0 && (
          <BrioAnalyticsSurface padding="xs" className="brio-mb-s">
            <div style={{ padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Mail size={16} style={{ color: '#e06090', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--brio-black)', fontFamily: font }}>
                    Client Wishes
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', fontFamily: font }}>
                    {clientEventItems.length} upcoming birthday{clientEventItems.length !== 1 ? 's' : ''} / anniversar{clientEventItems.length !== 1 ? 'ies' : 'y'}
                  </div>
                </div>
                <BrioBadge variant={clientEventItems.some(i => i.severity === 'critical') ? 'danger' : 'warning'}>
                  {clientEventItems.length} pending
                </BrioBadge>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {clientEventItems.map(item => {
                  const isBirthday = item.type === 'client_birthday';
                  return (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 10px',
                        background: isBirthday
                          ? 'rgba(224, 96, 144, 0.06)'
                          : 'rgba(74, 127, 165, 0.06)',
                        border: '1px solid var(--brio-border)',
                      }}
                    >
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{isBirthday ? '🎂' : '🎉'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 400, fontFamily: font }}>
                          {item.meta?.clientName || item.entity_label || '—'}
                        </div>
                        <div style={{ fontSize: 9, color: 'var(--brio-grey)', fontFamily: font }}>
                          {item.title} — {item.meta?.eventDate || ''}
                        </div>
                      </div>
                      <BrioBadge variant={item.severity === 'critical' ? 'danger' : 'accent'}>
                        {isBirthday ? 'Birthday' : 'Anniversary'}
                      </BrioBadge>
                      <button
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '4px 12px', fontSize: 10,
                          background: isBirthday ? '#e06090' : '#4a7fa5',
                          color: '#fff', border: 'none', cursor: 'pointer',
                          fontFamily: font, fontWeight: 500,
                        }}
                        onClick={(e) => { e.stopPropagation(); handleSendClientWishes(item); }}
                      >
                        <Mail size={10} /> Send Wishes
                      </button>
                      <button
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          padding: '4px 6px', fontSize: 10,
                          background: 'transparent', border: '1px solid var(--brio-border)',
                          cursor: 'pointer', color: 'var(--brio-grey)',
                        }}
                        title="Dismiss"
                        onClick={(e) => { e.stopPropagation(); handleResolve(item); }}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </BrioAnalyticsSurface>
        )}

        {/* ── Monthly Archive & CA Workflow (action card) ──────────── */}
        {caReminderItem && isAdmin && (
          <BrioAnalyticsSurface padding="xs" className="brio-mb-s">
            <div
              onClick={() => setArchivePanelOpen(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', cursor: 'pointer', userSelect: 'none',
              }}
            >
              <Archive size={16} style={{ color: 'var(--brio-gold)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--brio-black)', fontFamily: font }}>
                  {caReminderItem.title}
                </div>
                <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-grey)', marginTop: 2, fontFamily: font }}>
                  {caReminderItem.description}
                </div>
              </div>
              <BrioBadge variant={caReminderItem.severity === 'critical' ? 'danger' : caReminderItem.severity === 'warning' ? 'warning' : 'neutral'}>
                {caReminderItem.severity === 'critical' ? 'Overdue' : 'Action needed'}
              </BrioBadge>
              <button
                style={{
                  ...actionBtnGold,
                  padding: '6px 14px',
                  fontSize: 11,
                }}
                onClick={(e) => { e.stopPropagation(); setArchivePanelOpen(true); }}
              >
                <Archive size={11} /> Open Workflow
              </button>
            </div>
          </BrioAnalyticsSurface>
        )}

        {/* ── Filters ─────────────────────────────────────────────── */}
        <FilterBar filters={filters} setFilters={setFilters} projects={projects} />

        {/* ── Select all + Bulk actions ───────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', marginBottom: 4 }}>
          <div
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--brio-grey)', fontSize: 11, fontFamily: font }}
            onClick={handleSelectAll}
          >
            {selectedIds.size === 0
              ? <Square size={14} />
              : selectedIds.size === allVisibleItems.length
                ? <CheckSquare size={14} style={{ color: 'var(--brio-gold)' }} />
                : <MinusSquare size={14} style={{ color: 'var(--brio-gold)' }} />
            }
            <span style={{ fontWeight: 300 }}>
              {selectedIds.size === 0 ? 'Select all' : `${selectedIds.size} of ${visibleCount}`}
            </span>
          </div>
        </div>

        <BulkActionBar
          count={selectedIds.size}
          onAction={handleBulkAction}
          onClear={() => setSelectedIds(new Set())}
        />

        {/* ── Action error banner ──────────────────────────────────── */}
        {actionError && (
          <div style={{
            padding: '8px 12px', marginBottom: 8,
            background: '#fef2f2', border: '1px solid var(--brio-red)',
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 11, fontWeight: 300, color: 'var(--brio-red)', fontFamily: font,
          }}>
            <AlertTriangle size={13} />
            <span style={{ flex: 1 }}>{actionError}</span>
            <button style={{ ...actionBtn, color: 'var(--brio-red)' }} onClick={() => setActionError(null)}>
              <X size={11} />
            </button>
          </div>
        )}

        {/* ── Time-bucketed item list ─────────────────────────────── */}
        {['today', 'this_week', 'upcoming'].map(bucketKey => (
          <TimeBucket
            key={bucketKey}
            bucketKey={bucketKey}
            domainItems={filteredGrouped[bucketKey] || {}}
            fmtCurr={fmtCurr}
            collapsed={collapsedBuckets[bucketKey]}
            onToggle={() => toggleBucket(bucketKey)}
            selectedIds={selectedIds}
            hasSelection={hasSelection}
            onSelect={handleSelect}
            onAcknowledge={handleAcknowledge}
            onSnooze={handleSnooze}
            onResolve={handleResolve}
            onDismiss={handleDismiss}
            onNavigate={handleNavigate}
          />
        ))}

        {/* ── Data Completeness drawer (system domain, separate from main) ── */}
        <DataCompletenessDrawer
          items={dataCompletenessItems}
          fmtCurr={fmtCurr}
          selectedIds={selectedIds}
          hasSelection={hasSelection}
          onSelect={handleSelect}
          onAcknowledge={handleAcknowledge}
          onSnooze={handleSnooze}
          onResolve={handleResolve}
          onDismiss={handleDismiss}
          onNavigate={handleNavigate}
        />

        {/* ── Empty state ─────────────────────────────────────────── */}
        {visibleCount === 0 && dataCompletenessItems.length === 0 && (
          <BrioAnalyticsSurface padding="l">
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Shield size={28} style={{ color: 'var(--brio-grey)', marginBottom: 8 }} />
              <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--brio-black)', fontFamily: font }}>All clear</div>
              <div style={{ fontSize: 11, fontWeight: 300, color: 'var(--brio-grey)', marginTop: 4, fontFamily: font }}>
                No operational issues detected at this time.
              </div>
            </div>
          </BrioAnalyticsSurface>
        )}

        {/* ── Legend ───────────────────────────────────────────────── */}
        <div style={{ marginTop: 16, padding: '4px 0' }}>
          <div style={{ fontSize: 10, fontWeight: 200, color: 'var(--brio-grey)', display: 'flex', gap: 16, fontFamily: font }}>
            <span style={{ color: 'var(--brio-red)' }}>● Critical</span>
            <span style={{ color: 'var(--brio-gold)' }}>● Warning</span>
            <span style={{ color: 'var(--brio-teal)' }}>● Signal</span>
            <span style={{ color: 'var(--brio-orange)' }}>● Silent failure</span>
          </div>
        </div>
      </BrioFrame>

      {/* ── Dismiss Confirmation Modal ─────────────────────────────── */}
      {confirmDismiss && (
        <BrioModal
          title="Dismiss Radar Items"
          titleDanger
          onClose={() => setConfirmDismiss(null)}
          width="sm"
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={actionBtn} onClick={() => setConfirmDismiss(null)}>Cancel</button>
              <button style={{ ...actionBtn, background: 'var(--brio-red)', color: '#fff', border: '1px solid var(--brio-red)' }}
                onClick={confirmDismissAction}>
                <Trash2 size={11} /> Dismiss {confirmDismiss.length} {confirmDismiss.length === 1 ? 'item' : 'items'}
              </button>
            </div>
          }
        >
          <BrioModalBody>
            <p style={{ fontSize: 12, fontWeight: 300, color: 'var(--brio-black)', margin: '0 0 12px', fontFamily: font }}>
              Dismissed items will be permanently hidden from Radar. This is useful for old or irrelevant warnings (e.g. former employees who never tracked hours).
            </p>
            <p style={{ fontSize: 11, fontWeight: 300, color: 'var(--brio-grey)', margin: 0, fontFamily: font }}>
              This action cannot be undone from the dashboard. Items can be restored via database.
            </p>
            {confirmDismiss.length <= 10 && (
              <div style={{ marginTop: 12 }}>
                {confirmDismiss.map(item => (
                  <div key={item.id} style={{ fontSize: 11, fontWeight: 300, color: 'var(--brio-black)', padding: '2px 0', fontFamily: font }}>
                    • {item.title} — {item.entity_label || item.description}
                  </div>
                ))}
              </div>
            )}
          </BrioModalBody>
        </BrioModal>
      )}

      {/* ── Monthly Archive & CA Workflow Modal ─────────────────────── */}
      {archivePanelOpen && (
        <BrioModal
          title="Monthly Archive & Accountant Workflow"
          onClose={() => setArchivePanelOpen(false)}
          width="lg"
        >
          <BrioModalBody>
            <MonthlyArchivePanel
              invoices={invoices}
              fmtCurr={fmtCurr}
              currentUser={currentUser}
              projects={projects}
              invoicesHook={invoicesHook}
              radarSettingsHook={radarSettingsHook}
              onComplete={() => setArchivePanelOpen(false)}
            />
          </BrioModalBody>
        </BrioModal>
      )}
    </>
  );
}
